import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaStar } from 'react-icons/fa';
import { toast } from 'react-toastify';
import TenantService, { Location } from '../services/TenantService';
import LocationModal from './LocationModal';
import { personalakteStyles as ps, colors } from '../../../employees/styles/personalakteStyles';

export default function LocationsTab(): React.JSX.Element {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    setLoading(true);
    try {
      const data = await TenantService.getLocations();
      setLocations(data);
    } catch (err) {
      console.error('Failed to load locations:', err);
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingLocation(null);
    setModalOpen(true);
  };

  const handleEdit = (location: Location) => {
    setEditingLocation(location);
    setModalOpen(true);
  };

  const handleDelete = async (location: Location) => {
    if (location.is_headquarters) {
      toast.error('The headquarters cannot be deleted');
      return;
    }

    const confirmed = window.confirm(`Delete location "${location.name}"?`);
    if (!confirmed) return;

    try {
      await TenantService.deleteLocation(location.id);
      toast.success('Location deleted');
      loadLocations();
    } catch (err: any) {
      console.error('Failed to delete location:', err);
      const message = err?.response?.data?.detail || 'Delete failed';
      toast.error(message);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingLocation(null);
  };

  const handleModalSave = () => {
    setModalOpen(false);
    setEditingLocation(null);
    loadLocations();
  };

  const formatAddress = (loc: Location): string => {
    const houseNum = loc.house_number || loc.houseNumber || loc.street_number;
    const streetWithNumber = [loc.street, houseNum].filter(Boolean).join(' ');
    const postalCode = loc.postal_code || loc.postalCode;
    const parts = [streetWithNumber, postalCode, loc.city].filter(Boolean);
    return parts.join(' ') || '—';
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted }}>Loading...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: colors.navy }}>
          Locations ({locations.length})
        </h3>
        <button onClick={handleAdd} className="hover-btn-primary" style={ps.buttonPrimary}>
          <FaPlus size={11} />
          Add location
        </button>
      </div>

      {locations.length === 0 ? (
        <div style={{ ...ps.innerContainer, padding: 60, textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: 15, color: colors.textMuted }}>No locations yet</p>
          <p style={{ margin: '8px 0 0 0', fontSize: 13, color: colors.textLight }}>
            Click "Add location" to get started.
          </p>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Address</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Radius (m)</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Employees</th>
              <th style={{ ...thStyle, textAlign: 'center', width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {locations.map((loc) => (
              <tr key={loc.id} className="hover-list-item" style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, color: colors.navy }}>{loc.name}</span>
                    {loc.is_headquarters && (
                      <span style={ps.badge(colors.warningLight, '#92400e')}>
                        <FaStar size={9} />
                        HQ
                      </span>
                    )}
                  </div>
                </td>
                <td style={tdStyle}>{formatAddress(loc)}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{loc.radius_meters ?? '—'}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{loc.employee_count ?? '—'}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                    <button onClick={() => handleEdit(loc)} title="Edit" style={actionBtnStyle}>
                      <FaEdit size={13} />
                    </button>
                    <button
                      onClick={() => handleDelete(loc)}
                      title="Delete"
                      disabled={loc.is_headquarters}
                      style={{
                        ...actionBtnStyle,
                        color: loc.is_headquarters ? colors.textLight : colors.danger,
                        cursor: loc.is_headquarters ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <FaTrash size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <LocationModal
        isOpen={modalOpen}
        location={editingLocation}
        onClose={handleModalClose}
        onSave={handleModalSave}
      />
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: colors.textLight,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  borderBottom: `1px solid ${colors.border}`,
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: 13,
  color: colors.textBody,
};

const actionBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 6,
  cursor: 'pointer',
  color: colors.textMuted,
  borderRadius: 6,
  transition: 'background 0.15s',
};
